import { useState } from 'react';
import { GradientDropdownStrength } from '@/components/generic/dropdown/GradientDropdown';
import {
  IroIrremediabilityPicker_NegativeImpactFragment,
  IrremediabilityValue,
  useUpdateIroIrremediabilityMutation,
} from '@/graphql/generated';
import { useToast } from '@/components/layout/Toast';
import { useTranslation } from '@/hooks/useTranslation';
import i18n from '@/i18n';
import { useRefetchMaterialities } from './useRefetchMaterialities';
import { IroPickerDropdown } from '@/components/project/iro/evaluation/picker/IroPickerDropdown';

export type IroIrremediabilityItem = {
  id: IrremediabilityValue | null;
  name: string;
  strength: GradientDropdownStrength;
};

const irremediabilityCriteriaChoicesMap = (): Map<
  IrremediabilityValue | null,
  IroIrremediabilityItem
> =>
  new Map([
    [
      IrremediabilityValue.Irremediable,
      {
        id: IrremediabilityValue.Irremediable,
        name: i18n.t(
          `enum:iroCriteria.irremediability.${IrremediabilityValue.Irremediable}`,
        ),
        strength: GradientDropdownStrength.VeryStrong,
      },
    ],
    [
      IrremediabilityValue.HardlyRemediable,
      {
        id: IrremediabilityValue.HardlyRemediable,
        name: i18n.t(
          `enum:iroCriteria.irremediability.${IrremediabilityValue.HardlyRemediable}`,
        ),
        strength: GradientDropdownStrength.Strong,
      },
    ],
    [
      IrremediabilityValue.ModeratelyRemediable,
      {
        id: IrremediabilityValue.ModeratelyRemediable,
        name: i18n.t(
          `enum:iroCriteria.irremediability.${IrremediabilityValue.ModeratelyRemediable}`,
        ),
        strength: GradientDropdownStrength.Medium,
      },
    ],
    [
      IrremediabilityValue.EasilyRemediable,
      {
        id: IrremediabilityValue.EasilyRemediable,
        name: i18n.t(
          `enum:iroCriteria.irremediability.${IrremediabilityValue.EasilyRemediable}`,
        ),
        strength: GradientDropdownStrength.Light,
      },
    ],
    [
      null,
      {
        id: null,
        name: '-',
        strength: GradientDropdownStrength.VeryLight,
      },
    ],
  ]);

export const irremediabilityCriteriaChoices = () =>
  Array.from(irremediabilityCriteriaChoicesMap().values());

export function IroIrremediabilityPicker({
  iro,
  readOnly = false,
}: {
  iro: IroIrremediabilityPicker_NegativeImpactFragment;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const items = irremediabilityCriteriaChoicesMap();
  const initialIrremediabilityItem = items.get(
    iro.irremediabilityCriteria?.value || null,
  );
  const [selected, setSelected] = useState(initialIrremediabilityItem || null);
  const [update] = useUpdateIroIrremediabilityMutation();
  const toast = useToast();
  const refetchQueries = useRefetchMaterialities();

  const chooseItemId = (itemId: string | null) => {
    const item = items.get(itemId as IrremediabilityValue | null) || null;
    setSelected(item);

    update({
      variables: {
        input: {
          id: iro.id,
          irremediability: item?.id ?? null,
        },
      },
      refetchQueries,
    })
      .then(() => {
        toast.openToastWithMessage(
          t('toast:iro.criteria.update.success', {
            criteriaName: t('iro.criteria.irremediability.title'),
          }),
        );
      })
      .catch(() => {
        toast.openToastWithError(
          t('toast:iro.criteria.irremediability.update.error'),
        );
      });
  };

  return (
    <IroPickerDropdown<IroIrremediabilityItem>
      items={items}
      selected={selected}
      chooseItemId={chooseItemId}
      assessmentStats={iro.assessmentStats?.irremediability ?? null}
      readOnly={readOnly}
    />
  );
}
